import React from 'react';
import { Download } from 'lucide-react';

// Phase Workbook URLs
export const PHASE_WORKBOOKS = {
  1: {
    name: 'Stabilize',
    url: 'https://customer-assets.emergentagent.com/job_women-healing-hub/artifacts/hkb66zmo_sage_phase1_stabilize_workbook.pdf',
    description: 'Build safety in your nervous system'
  },
  2: {
    name: 'Understand',
    url: 'https://customer-assets.emergentagent.com/job_women-healing-hub/artifacts/hvnbh943_sage_phase2_understand_workbook.pdf',
    description: 'Make sense of what happened'
  },
  3: {
    name: 'Reclaim',
    url: 'https://customer-assets.emergentagent.com/job_women-healing-hub/artifacts/grby08by_sage_phase3_reclaim_workbook.pdf',
    description: 'Reconnect with who you are'
  },
  4: {
    name: 'Renew',
    url: 'https://customer-assets.emergentagent.com/job_women-healing-hub/artifacts/j8bjpdpk_sage_phase4_renew_workbook.pdf',
    description: 'Open to what comes next'
  }
};

// Mood colors
export const MOOD_COLORS = {
  stormy: '#1a1a3e',
  heavy: '#3d2b4a',
  cloudy: '#3a3a4a',
  lifting: '#2a4a4a',
  grounded: '#C9A84C',
};

// Phase info
export const PHASES = [
  { id: 1, name: 'Stabilize' },
  { id: 2, name: 'Understand' },
  { id: 3, name: 'Reclaim' },
  { id: 4, name: 'Renew' },
];

export const PhaseCard = ({ phase, currentPhase }) => {
  const isActive = phase.id === currentPhase;
  const isCompleted = phase.id < currentPhase;
  
  return (
    <div
      className={`p-4 rounded-xl transition-all ${isActive ? 'scale-105' : ''}`}
      style={{
        backgroundColor: isActive 
          ? 'rgba(201, 168, 76, 0.15)' 
          : isCompleted 
            ? 'rgba(138, 158, 143, 0.1)' 
            : '#F7F1EE',
        border: isActive ? '1px solid rgba(201, 168, 76, 0.3)' : '1px solid transparent'
      }}
      data-testid={`phase-card-${phase.id}`}
    >
      <div className="flex items-center gap-3">
        <div 
          className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium"
          style={{
            backgroundColor: isActive 
              ? '#C9A84C' 
              : isCompleted 
                ? '#72826F' 
                : '#EAE4DC',
            color: isActive || isCompleted ? '#FBF9F6' : '#6B6878'
          }}
        >
          {isCompleted ? '✓' : phase.id}
        </div>
        <div>
          <p className={`font-medium ${isActive ? 'text-[#1A1A2E]' : 'text-[#4A4A5A]'}`}>
            {phase.name}
          </p>
          {isActive && (
            <p className="text-[#6B6878] text-xs">Current phase</p>
          )}
        </div>
      </div>
    </div>
  );
};

export const MoodCalendar = ({ days }) => (
  <div className="grid grid-cols-7 gap-1">
    {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
      <div key={`header-${day}-${i}`} className="text-center text-[#8A8798] text-xs pb-1">{day}</div>
    ))}
    {days.map((day) => (
      <div
        key={day.date}
        className={`aspect-square rounded-lg flex items-center justify-center text-xs transition-all ${
          day.isToday ? 'ring-2 ring-white/30' : ''
        }`}
        style={{
          backgroundColor: day.mood 
            ? MOOD_COLORS[day.mood] || '#EAE4DC'
            : '#F7F1EE'
        }}
        title={day.mood ? `${day.date}: ${day.mood}` : day.date}
      >
        {day.isToday && <span className="text-[#2E2E42]">{day.dayNum}</span>}
      </div>
    ))}
  </div>
);

export const WorkbookCard = ({ workbook, phaseNumber, isCurrentPhase }) => (
  <a
    href={workbook.url}
    target="_blank"
    rel="noopener noreferrer"
    className={`block p-4 rounded-xl transition-all hover:scale-[1.02] ${
      isCurrentPhase ? '' : 'opacity-70 hover:opacity-100'
    }`}
    style={{
      backgroundColor: isCurrentPhase 
        ? 'rgba(201, 168, 76, 0.15)' 
        : '#F7F1EE',
      border: isCurrentPhase 
        ? '1px solid rgba(201, 168, 76, 0.3)' 
        : '1px solid transparent'
    }}
    data-testid={`workbook-${phaseNumber}`}
  >
    <div className="flex items-center justify-between">
      <div>
        <p className="font-medium text-[#1A1A2E]">{workbook.name}</p>
        <p className="text-[#6B6878] text-sm">{workbook.description}</p>
      </div>
      <Download className="w-5 h-5 text-[#6B6878]" />
    </div>
  </a>
);

export const StatCard = ({ label, value, icon: Icon }) => (
  <div 
    className="p-4 rounded-xl"
    style={{ backgroundColor: '#F7F1EE' }}
  >
    <div className="flex items-center gap-3">
      {Icon && <Icon className="w-5 h-5" style={{ color: '#C9A84C' }} />}
      <div>
        <p className="text-[#6B6878] text-sm">{label}</p>
        <p className="text-[#1A1A2E] font-semibold text-lg">{value}</p>
      </div>
    </div>
  </div>
);
